import { ShipmentTracking } from '@graphcommerce/graphql-mesh'
import { OrderCardFragment } from '@graphcommerce/magento-customer/components/OrderCard/OrderCard.gql'
import { OrderItemFragment } from '@graphcommerce/magento-customer/components/OrderItem/OrderItem.gql'
import { UseOrderCardItemImages } from '@graphcommerce/magento-customer/hooks/useOrderCardItemImages'
import { Money } from '@graphcommerce/magento-store'
import { extendableComponent, useDateTimeFormat } from '@graphcommerce/next-ui'
import { Trans } from '@lingui/react'
import { Box, Card, Skeleton, SxProps, Theme, styled } from '@mui/material'
import PageLink from 'next/link'
import { getEstShipDate } from '../../Order/OrderSummary/OrderSummary'
import { OrderItemSkeleton } from '../../Skeleton/OrderItemSkeleton'
import { OrderItem } from '../OrderItem/OrderItem'

type OrderCardProps = Partial<OrderCardFragment> & { estimated_ship_date?: string } & {
  status?: string
  loading?: boolean
  images?: UseOrderCardItemImages
  sx?: SxProps<Theme>
  reviews?: any[]
}

type TrackingDisplayLink = {
  trackingNumber?: string
  link?: string
}

const componentName = 'OrderCard' as const
const parts = [
  'orderContainer',
  'orderRow',
  'orderMoney',
  'orderProducts',
  'images',
  'placeholder',
  'buttonRoot',
  'tracking',
] as const
const { classes } = extendableComponent(componentName, parts)

const OrderContainer = styled(Box, { name: componentName, target: classes.orderContainer })(
  ({ theme }) => ({
    padding: theme.spacings.sm,
    display: 'grid',
    justifyContent: 'center',
    width: '100%',
  }),
)

const OrderRow = styled(Box, { name: componentName, target: classes.orderRow })(({ theme }) => ({
  margin: `0 auto calc(${theme.spacings.xxs} * .5) auto`,
  display: 'flex',
  gap: theme.spacings.xxs,
}))

export const getShipment = (
  product_sku: string | undefined,
  shipments,
): ShipmentTracking | null | undefined => {
  let firstShipment: string | null | undefined = null
  shipments?.map((shipment) => {
    if (shipment?.items?.find((item) => item?.product_sku === product_sku)) {
      firstShipment = shipment.tracking?.at(0)
    }
    return null
  })
  return firstShipment
}

export function OrderCard(props: OrderCardProps) {
  const {
    number,
    shipments,
    total,
    items,
    order_date,
    status,
    estimated_ship_date,
    images,
    loading,
    sx = [],
    reviews,
  } = props

  const dateFormatter = useDateTimeFormat({ dateStyle: 'long' })

  if (loading) {
    return <OrderItemSkeleton />
  }

  const trackingUrls: TrackingDisplayLink[] = []
  if (items) {
    items.forEach((item) => {
      const shipment = getShipment(item?.product_sku, shipments)
      if (shipment?.carrier && shipment.number) {
        if (shipment?.carrier.includes('ups')) {
          trackingUrls.push({
            trackingNumber: shipment?.number ?? '',
            link: `https://www.ups.com/track?track=yes&trackNums=${shipment.number}`,
          })
        } else if (shipment?.carrier.includes('fedex')) {
          trackingUrls.push({
            trackingNumber: shipment?.number ?? '',
            link: `https://www.fedex.com/apps/fedextrack/?action=track&trackingnumber=${shipment?.number}`,
          })
        } else {
          trackingUrls.push({
            trackingNumber: shipment?.number ?? '',
            link: `https://www.tforcefreight.com/ltl/apps/Tracking?proNumbers=${shipment?.number}`,
          })
        }
      }
    })
  }

  return (
    <Card elevation={3} className='rounded-[10px] bg-[#00000006]'>
      <div className='mx-5 items-center justify-start border-b-2 border-light-gray p-[10px] md:flex md:p-5 md:pb-2'>
        <div className='Type-Large-Regular flex flex-col items-center gap-[4px] md:flex-row md:gap-[55px] '>
          <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
            <p className='text-tight-black'>
              <Trans id='Order Number:' />
            </p>
            <PageLink href={`/account/orders/view?orderId=${number}`} passHref>
              <span className=' text-70-grey'>#{number}</span>
            </PageLink>
          </div>
          <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
            <p className=' text-tight-black'>
              <Trans id='Order Date' />
            </p>
            <span className='text-70-grey'>{dateFormatter.format(new Date(order_date ?? ''))}</span>
          </div>
          <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
            <p className=' text-tight-black'>
              <Trans id='Amount:' />
            </p>
            <span className='text-70-grey'>
              <Money {...total?.grand_total} />
            </span>
          </div>
          <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
            <p className=' text-tight-black'>
              <Trans id='Est. Ship Date:' />
            </p>
            <span className='text-70-grey'>
              {estimated_ship_date
                ? getEstShipDate({
                    estimated_ship_date,
                  })
                : 'N/A'}
            </span>
          </div>
          {trackingUrls && trackingUrls?.length > 0 && (
            <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
              <p className=' text-tight-black'>
                <Trans id='Tracking' />
              </p>
              <div className='flex flex-col'>
                <a
                  className='cursor-pointer text-material-ui-blue hover:text-material-ui-dark-blue hover:underline'
                  href={trackingUrls[0].link}
                  target='_blank'
                  rel='noreferrer'
                >
                  {trackingUrls[0].trackingNumber}
                </a>
              </div>
            </div>
          )}
          {/* <div className='grid w-full grid-cols-2 items-center md:flex md:w-[unset] md:flex-col md:items-start md:justify-center md:gap-2'>
            <p className=' text-tight-black'>
              <Trans id='Ship Method:' />
            </p>
            <span className='text-70-grey'>{shipments?.[0]?.tracking?.[0]?.carrier ?? 'N/A'}</span>
          </div> */}
        </div>
      </div>
      <div className='px-[25px]'>
        {items?.map((item) => (
          <OrderItem
            {...(item as unknown as Omit<OrderItemFragment, 'status'>)}
            status={status}
            {...images?.[item?.product_url_key ?? '']}
            shipment={getShipment(item?.product_sku, shipments)}
            orderId={number}
            reviews={reviews}
          />
        ))}
      </div>
    </Card>
  )
}
